import { useState, useCallback, useMemo } from 'react';
import type { DetectionResult, MetricField, SprintInfo } from '../../utils/sprintImport';
import { ALL_METRIC_FIELDS, METRIC_LABELS } from '../../utils/sprintImport';

interface SprintMetrics {
  costsPlanned: number;
  costsActual: number;
  revenuePlanned: number;
  revenueActual: number;
  storyPointsPlanned: number;
  storyPointsTarget: number;
  storyPointsAchieved: number;
  manDaysPlanned: number;
  manDaysActual: number;
}

interface SprintWithMetrics {
  sprintId: string;
  name: string;
  startDate: string;
  endDate: string;
  piNumber?: number;
  metrics?: SprintMetrics;
}

interface BulkEditModalProps {
  sprints: SprintWithMetrics[];
  sprintInfos?: SprintInfo[];
  teamId?: string;
  importData?: DetectionResult | null;
  onSave: (items: Array<{ sprintId: string; metrics: Partial<SprintMetrics> }>) => void;
  onClose: () => void;
  onDownloadTemplate: () => void;
  isSaving: boolean;
}

interface CellValue {
  value: string;
  changed: boolean;
  error: string | null;
}

type CellGrid = Record<string, Record<MetricField, CellValue>>;

const COLUMN_GROUPS = [
  { label: 'Costs', fields: ['costsPlanned', 'costsActual'] as MetricField[] },
  { label: 'Revenue', fields: ['revenuePlanned', 'revenueActual'] as MetricField[] },
  { label: 'Story Points', fields: ['storyPointsPlanned', 'storyPointsTarget', 'storyPointsAchieved'] as MetricField[] },
  { label: 'Man-Days', fields: ['manDaysPlanned', 'manDaysActual'] as MetricField[] },
];

const SP_FIELDS: MetricField[] = ['storyPointsPlanned', 'storyPointsTarget', 'storyPointsAchieved'];

function validateCell(field: MetricField, value: string): string | null {
  if (value.trim() === '') return null; // Empty = no change
  const num = parseFloat(value);
  if (isNaN(num)) return 'Must be a number';
  if (num < 0) return 'Must be >= 0';
  if (SP_FIELDS.includes(field) && !Number.isInteger(num)) return 'Must be a whole number';
  return null;
}

function getStep(field: MetricField): string {
  if (SP_FIELDS.includes(field)) return '1';
  if (field === 'manDaysPlanned' || field === 'manDaysActual') return '0.5';
  return '0.01';
}

export default function BulkEditModal({
  sprints,
  importData,
  onSave,
  onClose,
  onDownloadTemplate,
  isSaving,
}: BulkEditModalProps) {
  // Column visibility toggles
  const [visibleGroups, setVisibleGroups] = useState<Record<string, boolean>>({
    Costs: true,
    Revenue: true,
    'Story Points': true,
    'Man-Days': true,
  });

  // Build initial grid from existing metrics + import data
  const initialGrid = useMemo(() => {
    const grid: CellGrid = {};

    for (const sprint of sprints) {
      grid[sprint.sprintId] = {} as Record<MetricField, CellValue>;
      for (const field of ALL_METRIC_FIELDS) {
        const existing = sprint.metrics?.[field];
        grid[sprint.sprintId][field] = {
          value: existing !== undefined && existing !== 0 ? String(existing) : '',
          changed: false,
          error: null,
        };
      }
    }

    // Overlay import data if provided
    if (importData) {
      for (const row of importData.rows) {
        const sprint = row.sprintMatch.sprint;
        if (!sprint) continue;
        if (!grid[sprint.sprintId]) continue;

        for (const field of ALL_METRIC_FIELDS) {
          const val = row.values[field];
          if (val !== undefined && val !== null) {
            const str = String(val);
            grid[sprint.sprintId][field] = {
              value: str,
              changed: grid[sprint.sprintId][field].value !== str,
              error: null,
            };
          }
        }
      }
    }

    return grid;
  }, [sprints, importData]);

  const [grid, setGrid] = useState<CellGrid>(initialGrid);

  const visibleFields = useMemo(
    () => COLUMN_GROUPS.filter((g) => visibleGroups[g.label]).flatMap((g) => g.fields),
    [visibleGroups]
  );

  const handleCellChange = useCallback((sprintId: string, field: MetricField, value: string) => {
    setGrid((prev) => ({
      ...prev,
      [sprintId]: {
        ...prev[sprintId],
        [field]: {
          value,
          changed: value !== (initialGrid[sprintId]?.[field]?.value ?? ''),
          error: null, // Clear error while typing
        },
      },
    }));
  }, [initialGrid]);

  const handleCellBlur = useCallback((sprintId: string, field: MetricField) => {
    setGrid((prev) => {
      const cell = prev[sprintId][field];
      return {
        ...prev,
        [sprintId]: {
          ...prev[sprintId],
          [field]: {
            ...cell,
            error: validateCell(field, cell.value),
          },
        },
      };
    });
  }, []);

  // Compute stats
  const { changedCount, newCount, updatedCount, errorCount, changedItems } = useMemo(() => {
    let changed = 0;
    let newSprints = 0;
    let updated = 0;
    let errors = 0;
    const items: Array<{ sprintId: string; metrics: Partial<SprintMetrics> }> = [];

    for (const sprint of sprints) {
      const row = grid[sprint.sprintId];
      if (!row) continue;

      let hasChange = false;
      const metrics: Partial<SprintMetrics> = {};

      for (const field of visibleFields) {
        const cell = row[field];
        if (cell.error) errors++;
        if (cell.changed && cell.value.trim() !== '') {
          hasChange = true;
          metrics[field] = parseFloat(cell.value);
        }
      }

      if (hasChange) {
        changed++;
        if (sprint.metrics) updated++;
        else newSprints++;
        items.push({ sprintId: sprint.sprintId, metrics });
      }
    }

    return { changedCount: changed, newCount: newSprints, updatedCount: updated, errorCount: errors, changedItems: items };
  }, [grid, sprints, visibleFields]);

  const handleSave = () => {
    if (errorCount > 0 || changedItems.length === 0) return;
    onSave(changedItems);
  };

  // Group sprints by PI for display
  const groupedSprints = useMemo(() => {
    const piMap = new Map<number, SprintWithMetrics[]>();
    for (const sprint of sprints) {
      const pi = sprint.piNumber || 0;
      if (!piMap.has(pi)) piMap.set(pi, []);
      piMap.get(pi)!.push(sprint);
    }
    return Array.from(piMap.entries()).sort(([a], [b]) => a - b);
  }, [sprints]);

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-dark-700 rounded-lg shadow-xl w-[95vw] max-w-7xl max-h-[90vh] flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-dark-500">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Bulk Edit Sprint Metrics</h2>
          <button onClick={onClose} className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 rounded">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Column toggles */}
        <div className="flex items-center gap-4 px-6 py-3 bg-gray-50 dark:bg-dark-600 border-b border-gray-200 dark:border-dark-500 flex-wrap">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Columns:</span>
          {COLUMN_GROUPS.map((group) => (
            <label key={group.label} className="inline-flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={visibleGroups[group.label]}
                onChange={(e) => setVisibleGroups((prev) => ({ ...prev, [group.label]: e.target.checked }))}
                className="rounded border-gray-300 dark:border-dark-400 text-primary-500 focus:ring-primary-500"
              />
              <span className="text-xs text-gray-700 dark:text-gray-300">{group.label}</span>
            </label>
          ))}
          <div className="flex-1" />
          <button onClick={onDownloadTemplate} className="text-xs text-accent-blue hover:underline flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download Template
          </button>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto px-6 py-3">
          <table className="w-full border-collapse">
            <thead className="sticky top-0 bg-white dark:bg-dark-700 z-10">
              <tr className="border-b-2 border-gray-200 dark:border-dark-500">
                <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 sticky left-0 bg-white dark:bg-dark-700 z-20 min-w-[140px]">
                  Sprint
                </th>
                {visibleFields.map((field) => (
                  <th key={field} className="text-center py-2 px-2 text-xs font-medium text-gray-500 dark:text-gray-400 min-w-[100px]">
                    {METRIC_LABELS[field]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groupedSprints.map(([piNum, piSprints]) => (
                <PiGroup
                  key={piNum}
                  piNumber={piNum}
                  sprints={piSprints}
                  visibleFields={visibleFields}
                  grid={grid}
                  onCellChange={handleCellChange}
                  onCellBlur={handleCellBlur}
                  showPiHeader={groupedSprints.length > 1}
                />
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-dark-500 bg-gray-50 dark:bg-dark-600">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {changedCount > 0 ? (
              <>
                {changedCount} of {sprints.length} sprint{sprints.length !== 1 ? 's' : ''} modified
                {newCount > 0 && <span className="text-green-600 dark:text-green-400"> ({newCount} new)</span>}
                {updatedCount > 0 && <span className="text-amber-600 dark:text-amber-400"> ({updatedCount} updated)</span>}
              </>
            ) : (
              'No changes'
            )}
            {errorCount > 0 && (
              <span className="ml-2 text-red-600 dark:text-red-400">
                — {errorCount} error{errorCount !== 1 ? 's' : ''}, fix before saving
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="btn-outline text-sm">Cancel</button>
            <button
              onClick={handleSave}
              className="btn-primary text-sm"
              disabled={isSaving || errorCount > 0 || changedCount === 0}
            >
              {isSaving ? 'Saving...' : `Save All Changes${changedCount > 0 ? ` (${changedCount})` : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PiGroup({
  piNumber,
  sprints,
  visibleFields,
  grid,
  onCellChange,
  onCellBlur,
  showPiHeader,
}: {
  piNumber: number;
  sprints: SprintWithMetrics[];
  visibleFields: MetricField[];
  grid: CellGrid;
  onCellChange: (sprintId: string, field: MetricField, value: string) => void;
  onCellBlur: (sprintId: string, field: MetricField) => void;
  showPiHeader: boolean;
}) {
  return (
    <>
      {showPiHeader && piNumber > 0 && (
        <tr>
          <td
            colSpan={visibleFields.length + 1}
            className="py-2 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-dark-600"
          >
            PI {piNumber}
          </td>
        </tr>
      )}
      {sprints.map((sprint) => (
        <tr key={sprint.sprintId} className="border-b border-gray-100 dark:border-dark-600 hover:bg-gray-50 dark:hover:bg-dark-600/50">
          <td className="py-1.5 px-3 sticky left-0 bg-white dark:bg-dark-700 z-10">
            <div className="text-sm font-medium text-gray-900 dark:text-white">{sprint.name}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {new Date(sprint.startDate).toLocaleDateString()} – {new Date(sprint.endDate).toLocaleDateString()}
            </div>
          </td>
          {visibleFields.map((field) => {
            const cell = grid[sprint.sprintId]?.[field];
            if (!cell) return <td key={field} className="py-1.5 px-2" />;
            return (
              <td key={field} className="py-1.5 px-2">
                <div className="relative">
                  <input
                    type="number"
                    step={getStep(field)}
                    className={`w-full text-right text-sm py-1 px-2 rounded border ${
                      cell.error
                        ? 'border-red-400 dark:border-red-500 bg-red-50 dark:bg-red-900/20'
                        : cell.changed
                          ? 'border-amber-300 dark:border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                          : 'border-gray-200 dark:border-dark-400 bg-white dark:bg-dark-600'
                    } focus:outline-none focus:ring-1 focus:ring-primary-500`}
                    value={cell.value}
                    onChange={(e) => onCellChange(sprint.sprintId, field, e.target.value)}
                    onBlur={() => onCellBlur(sprint.sprintId, field)}
                  />
                  {cell.error && (
                    <div className="absolute top-full left-0 mt-0.5 text-xs text-red-600 dark:text-red-400 whitespace-nowrap">
                      {cell.error}
                    </div>
                  )}
                </div>
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}
